/**
 * @module features/users/components/EditUserModal
 * @description Responsive Drawer/Sheet for updating existing user profile details, roles,
 * contact information, and configurable FIDO2 passkey device limits.
 * On mobile, opens as a bottom sheet. On desktop/tablet, slides out from the right.
 */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserDto } from "@/types/auth";
import { UpdateUserInput } from "@/types/users";
import { useUpdateUser } from "../api/users-api";
import { AppError } from "@/lib/api-client";
import { Loader2, AlertCircle, Shield, User as UserIcon, Fingerprint, ShieldOff, KeyRound } from "lucide-react";

interface EditUserModalProps {
  user: UserDto | null;
  open: boolean;
  onClose: () => void;
  onDisableTotp?: (user: UserDto) => void;
}

interface FormValues {
  name: string;
  role: "super_admin" | "client";
  country_code: string;
  contact_number: string;
  max_passkeys: number;
}

export function EditUserModal({ user, open, onClose, onDisableTotp }: EditUserModalProps) {
  const updateUserMutation = useUpdateUser();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      role: "client",
      country_code: "+1",
      contact_number: "",
      max_passkeys: 5,
    },
  });

  const selectedRole = watch("role");
  const maxPasskeys = watch("max_passkeys");

  React.useEffect(() => {
    if (user) {
      reset({
        name: user.name,
        role: (user.role as "super_admin" | "client") || "client",
        country_code: user.country_code || "+1",
        contact_number: user.contact_number || "",
        max_passkeys: user.max_passkeys || 5,
      });
      setErrorMessage(null);
    }
  }, [user, reset]);

  const onSubmit = async (values: FormValues) => {
    if (!user) return;
    setErrorMessage(null);

    const payload: UpdateUserInput = {
      name: values.name.trim(),
      role: values.role,
      country_code: values.country_code.trim(),
      contact_number: values.contact_number.trim(),
      max_passkeys: Number(values.max_passkeys) || 5,
    };

    try {
      await updateUserMutation.mutateAsync({ userId: user.id, data: payload });
      onClose();
    } catch (err: any) {
      if (err instanceof AppError) {
        setErrorMessage(err.errorDetails);
      } else {
        setErrorMessage("Failed to update user. Please check your inputs.");
      }
    }
  };

  const handleClose = () => {
    setErrorMessage(null);
    onClose();
  };

  return (
    <Drawer
      open={open && Boolean(user)}
      onClose={handleClose}
      title="Edit User"
      description={user ? `Editing ${user.email_id}` : undefined}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {errorMessage && (
          <div className="flex items-center gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Role Selection */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground block">
            Account Role
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setValue("role", "client")}
              className={`flex items-center gap-2.5 p-3 rounded-lg border text-xs font-medium transition-all ${
                selectedRole === "client"
                  ? "border-purple-500 bg-purple-500/10 text-purple-600 dark:text-purple-400 font-semibold ring-1 ring-purple-500"
                  : "border-border bg-card text-muted-foreground hover:bg-muted"
              }`}
            >
              <UserIcon className="h-4 w-4 shrink-0" />
              <span>Client User</span>
            </button>
            <button
              type="button"
              onClick={() => setValue("role", "super_admin")}
              className={`flex items-center gap-2.5 p-3 rounded-lg border text-xs font-medium transition-all ${
                selectedRole === "super_admin"
                  ? "border-zinc-950 dark:border-zinc-100 bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold ring-1 ring-zinc-950 dark:ring-zinc-100"
                  : "border-border bg-card text-muted-foreground hover:bg-muted"
              }`}
            >
              <Shield className="h-4 w-4 shrink-0" />
              <span>Super Admin</span>
            </button>
          </div>
        </div>

        {/* Full Name */}
        <div className="space-y-1">
          <label htmlFor="edit-name" className="text-xs font-medium text-foreground block">
            Full Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="edit-name"
            placeholder="e.g. Jane Doe"
            {...register("name", { required: "Name is required." })}
            className="h-10 text-sm bg-card"
          />
          {errors.name && (
            <p className="text-[11px] text-destructive">{errors.name.message}</p>
          )}
        </div>

        {/* Email (Read-only) */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-foreground block">
            Email Address
          </label>
          <Input
            value={user?.email_id || ""}
            disabled
            className="h-10 text-sm bg-muted/50 font-mono text-muted-foreground"
          />
        </div>

        {/* Contact Details */}
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1 col-span-1">
            <label
              htmlFor="edit-country_code"
              className="text-xs font-medium text-foreground block"
            >
              Code
            </label>
            <Input
              id="edit-country_code"
              placeholder="+1"
              {...register("country_code")}
              className="h-10 text-sm font-mono bg-card"
            />
          </div>
          <div className="space-y-1 col-span-2">
            <label
              htmlFor="edit-contact_number"
              className="text-xs font-medium text-foreground block"
            >
              Contact Number
            </label>
            <Input
              id="edit-contact_number"
              type="tel"
              inputMode="tel"
              placeholder="555-0199"
              {...register("contact_number")}
              className="h-10 text-sm font-mono bg-card"
            />
          </div>
        </div>

        {/* Configurable Passkey Device Limit */}
        <div className="rounded-xl border border-border/80 dark:border-zinc-800 bg-muted/20 dark:bg-zinc-900/40 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="edit-max_passkeys" className="text-xs font-medium text-foreground flex items-center gap-1.5">
              <Fingerprint className="h-4 w-4 text-foreground shrink-0" />
              <span>Passkey Device Limit</span>
            </label>
            <span className="text-[11px] font-mono text-muted-foreground">
              {user?.passkey_count || 0} / {maxPasskeys || 5} Enrolled
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Input
              id="edit-max_passkeys"
              type="number"
              min={1}
              max={20}
              {...register("max_passkeys", { valueAsNumber: true, min: 1, max: 20 })}
              className="h-8 w-16 text-center font-mono text-xs bg-card"
            />
            <div className="flex items-center gap-1.5 flex-wrap">
              {[1, 3, 5, 10].map((count) => (
                <button
                  key={count}
                  type="button"
                  onClick={() => setValue("max_passkeys", count)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${
                    Number(maxPasskeys) === count
                      ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 border-zinc-950 dark:border-zinc-100 font-semibold shadow-xs"
                      : "bg-card border-border hover:bg-muted text-muted-foreground"
                  }`}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* TOTP 2FA Management */}
        {user?.is_totp_enabled && onDisableTotp && (
          <div className="rounded-xl border border-border/80 dark:border-zinc-800 bg-muted/20 dark:bg-zinc-900/40 p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <KeyRound className="h-4 w-4 text-purple-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground truncate">Authenticator 2FA</p>
                <p className="text-[11px] text-muted-foreground truncate">Active for this account</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                onClose();
                onDisableTotp(user);
              }}
              className="h-8 text-xs text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive shrink-0"
            >
              <ShieldOff className="h-3.5 w-3.5 mr-1.5" />
              Disable 2FA
            </Button>
          </div>
        )}

        {/* Actions Footer */}
        <div className="pt-3 flex items-center justify-end gap-2 border-t border-border mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="h-9 px-4 text-xs"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={updateUserMutation.isPending}
            className="h-9 px-4 text-xs min-w-[120px] shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
          >
            {updateUserMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
